import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { api, formatApiError } from '../lib/api';
import { suggestCorrectEmail } from '../lib/emailValidation';
import { getStoredInviteCode } from '../lib/referral';
import { Eye, EyeOff, ShieldCheck, Briefcase, Network, Banknote, FileCheck2 } from 'lucide-react';
import { usePageSEO } from '../lib/usePageSEO';
import { PARTNER_FAQ_JSONLD } from '../lib/seoData';
import { TaxFieldsForm } from '../components/TaxFieldsForm';

const BUSINESS_TYPES = [
  { id: 'funeral_home', label: 'Funeral home' },
  { id: 'cemetery', label: 'Cemetery' },
  { id: 'memorial_business', label: 'Memorial business' },
  { id: 'other', label: 'Other' },
];

/**
 * /become-a-partner
 *
 * A dedicated, professional page for funeral homes, cemeteries, memorial
 * providers, and regional distributors. Marketing content sits on top of
 * a full partner application form. Account types created here are strictly
 * `regional_partner` or `memorial_partner` — never `family`.
 */
export default function BecomeAPartner() {
  usePageSEO({
    title: 'Become a Partner — MyLeaf Legacy for Funeral Homes & Cemeteries',
    description: 'Earn recurring revenue with every memorial. Join MyLeaf as a Memorial Partner ($100 commission) or Regional Partner (12-month override territories). No startup cost, no monthly fees.',
    path: '/become-a-partner',
    jsonLd: PARTNER_FAQ_JSONLD,
  });
  const [sp] = useSearchParams();
  const formRef = useRef(null);

  const initialType = (() => {
    const v = (sp.get('as') || '').toLowerCase();
    if (v === 'memorial_partner' || v === 'memorial' || v === 'professional') return 'memorial_partner';
    return 'regional_partner';
  })();

  const [accountType, setAccountType] = useState(initialType);

  // Common
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Business
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('funeral_home');
  const [legalBusinessName, setLegalBusinessName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [taxConfirmation, setTaxConfirmation] = useState(false);
  // iter94 — multi-country tax fields. Country toggles which sibling
  // fields below are required + rendered.
  const [taxCountry, setTaxCountry] = useState('US');
  const [taxContractorType, setTaxContractorType] = useState('');
  const [taxSin, setTaxSin] = useState('');
  const [taxBusinessNumber, setTaxBusinessNumber] = useState('');
  const [taxCorporationName, setTaxCorporationName] = useState('');
  const [taxForeignId, setTaxForeignId] = useState('');
  const [taxBusinessRegistration, setTaxBusinessRegistration] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [stateRegion, setStateRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');

  // iter53 — Regional Partners pick Individual / Sole Proprietor OR
  // LLC / Corporation. Memorial Partners are always businesses.
  const [partnerType, setPartnerType] = useState('business');

  // iter53 — Independent Contractor Agreement acceptance. We capture this
  // as a separate checkbox on top of the existing tax confirmation. The
  // backend logs timestamp + IP + version on the user record + application.
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const AGREEMENT_VERSION = 'v1.1';

  // iter55 — Typed full-name signature. Required for all Partner signups
  // BEFORE the checkbox. The backend persists this verbatim and the PDF
  // renders it on the signature line. Family signups never use this field.
  const [signatureFullName, setSignatureFullName] = useState('');

  // Memorial-partner: invite code from a Regional Partner (URL param, with
  // iter101 localStorage fallback so the code survives page navigation).
  const [inviteCode, setInviteCode] = useState(
    ((sp.get('invite') || '').toUpperCase() || getStoredInviteCode()),
  );
  const [inviteValid, setInviteValid] = useState(null);

  // iter80 — Trackable invite token (`?inv=...&kind=memorial_partner`).
  // Wins over the typed 6-char invite_code. Pre-validated server-side.
  const inviteToken = (sp.get('inv') || '').trim();
  const [inviteTokenInfo, setInviteTokenInfo] = useState(null);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/invites/validate?token=${encodeURIComponent(inviteToken)}`)
      .then(({ data }) => setInviteTokenInfo(data))
      .catch(() => setInviteTokenInfo({ valid: false }));
  }, [inviteToken]);

  // Pre-fill MP fields from a trackable invite.
  useEffect(() => {
    if (inviteTokenInfo?.valid) {
      if (inviteTokenInfo.target_email && !email) setEmail(inviteTokenInfo.target_email);
      if (inviteTokenInfo.target_name && !name) setName(inviteTokenInfo.target_name);
      if (inviteTokenInfo.target_business_name && !businessName) {
        setBusinessName(inviteTokenInfo.target_business_name);
      }
      // iter89 — surface the Regional Partner's invite code in the
      // visible field so the FH sees who they're being attributed to.
      // Server-side attribution is already locked via the trackable
      // token, but an empty Invite Code field broke FH trust during
      // signup and looked like the link wasn't working.
      if (inviteTokenInfo.inviter_invite_code && !inviteCode) {
        setInviteCode(inviteTokenInfo.inviter_invite_code);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteTokenInfo]);

  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const nav = useNavigate();

  const isMemorial = accountType === 'memorial_partner';

  // Live-validate the invite code as the Memorial Partner types it.
  // iter89.1 — 4-digit numeric codes (was: 6+ char alphanumeric).
  // The previous `length < 6` guard silently bricked the Submit button
  // because validation never fired for the new short codes.
  useEffect(() => {
    if (!isMemorial) {
      setInviteValid(null);
      return;
    }
    const trimmed = inviteCode.trim();
    // Accept either new 4-digit numeric OR legacy 8-char alphanumeric
    // (back-compat for business cards still in circulation).
    const looksValid = /^[0-9]{4}$/.test(trimmed) || trimmed.length >= 6;
    if (!looksValid) {
      setInviteValid(null);
      return;
    }
    let cancelled = false;
    const id = setTimeout(async () => {
      try {
        const { data } = await api.post('/partners/invite-code/validate', { code: trimmed });
        if (!cancelled) setInviteValid(data);
      } catch {
        if (!cancelled) setInviteValid({ valid: false });
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(id); };
  }, [inviteCode, isMemorial]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try {
      const payload = {
        name,
        email,
        phone,
        password,
        account_type: accountType,
        // iter53: Regional Partners can be Individual or LLC/Corp. Memorial
        // Partners are always business entities (funeral homes / cemeteries).
        partner_type: isMemorial ? 'business' : partnerType,
        business_name: businessName.trim(),
        business_type: businessType,
        legal_business_name: legalBusinessName.trim(),
        tax_id: taxId.replace(/\D/g, ''),
        tax_confirmation: taxConfirmation,
        // iter94 — multi-country tax fields
        tax_country: taxCountry,
        tax_contractor_type: taxContractorType || null,
        tax_sin: taxSin.trim() || null,
        tax_business_number: taxBusinessNumber.trim() || null,
        tax_corporation_name: taxCorporationName.trim() || null,
        tax_foreign_id: taxForeignId.trim() || null,
        tax_business_registration: taxBusinessRegistration.trim() || null,
        address_line1: addressLine1.trim() || null,
        city: city.trim() || null,
        state: stateRegion.trim() || null,
        postal_code: postalCode.trim() || null,
        country: country.trim() || null,
        // iter53 — Independent Contractor Agreement.
        agreement_accepted: agreementAccepted,
        agreement_version: AGREEMENT_VERSION,
        // iter55 — typed full-name signature.
        signature_full_name: signatureFullName.trim(),
      };
      if (isMemorial) {
        // iter80 — token wins if present. Backend ignores invite_code
        // when invite_token is valid.
        if (inviteTokenInfo?.valid) {
          payload.invite_token = inviteToken;
        }
        // iter89.1 — codes are now 4-digit numeric. `.toUpperCase()` is
        // harmless for digits and still helps any legacy 8-char codes
        // that might be on old business cards.
        payload.invite_code = inviteCode.trim().toUpperCase();
      }
      await register(payload);
      nav(isMemorial ? '/partners/memorial' : '/partners/regional', { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="become-a-partner-page">
      <Navbar />

      {/* HERO — professional, business-focused */}
      <section
        className="relative overflow-hidden border-b border-[hsl(var(--primary)/0.08)]"
        style={{
          background:
            'linear-gradient(180deg, #FDFBF7 0%, #F5EFE2 100%)',
        }}
        data-testid="partner-hero"
      >
        <div className="ml-container py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--accent))] font-semibold">
              MyLeaf for Funeral Homes &amp; Cemeteries
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-[58px] leading-[1.1] mt-5 text-[hsl(var(--primary))]">
              Help families preserve a life — and build
              <span className="italic text-[hsl(var(--primary)/0.7)]"> recurring revenue </span>
              with every memorial.
            </h1>
            <p className="mt-7 text-[16px] sm:text-[17px] leading-relaxed text-[hsl(var(--primary)/0.78)] font-light max-w-2xl">
              MyLeaf partners with funeral homes, cemeteries, and memorial providers to offer
              families a beautiful, permanent home for the people they love — a QR-linked
              memorial that lives long after the service is over.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={scrollToForm}
                className="ml-btn-primary"
                data-testid="partner-hero-apply-btn"
              >
                Apply to become a partner
              </button>
              <a
                href="#partner-model"
                className="ml-btn-outline"
                data-testid="partner-hero-learn-more"
              >
                Learn more
              </a>
            </div>
            <div className="mt-8 flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.55)] font-medium">
              <ShieldCheck className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.4} />
              Payouts secured by Stripe Connect · W-9 collected on approval
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="ml-section-sacred" id="partner-model">
        <div className="ml-container">
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <Briefcase className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.4} />,
                title: 'A real revenue stream.',
                body: 'Commissions are paid on every memorial sold through your invite codes — automatically, every time a family completes checkout.',
              },
              {
                icon: <Network className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.4} />,
                title: 'A premium service for the families you serve.',
                body: 'Offer something families increasingly ask for: a permanent online memorial that pairs with their headstone or plaque, with a private vault for the family.',
              },
              {
                icon: <Banknote className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.4} />,
                title: 'No invoicing. No chasing payouts.',
                body: 'Stripe Connect handles payouts and year-end 1099 reporting on our side. You focus on families — we handle the back office.',
              },
            ].map((b) => (
              <div key={b.title} className="text-left" data-testid={`partner-value-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
                <div className="inline-flex w-12 h-12 rounded-full bg-[hsl(var(--accent))]/12 items-center justify-center">
                  {b.icon}
                </div>
                <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-5 leading-snug">
                  {b.title}
                </h3>
                <p className="mt-3 text-[14.5px] text-[hsl(var(--primary)/0.7)] leading-relaxed font-light">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIER COMPARISON — Regional vs Memorial */}
      <section className="ml-section-sacred">
        <div className="ml-container">
          <div className="max-w-2xl">
            <div className="ml-overline">Two ways to partner</div>
            <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-5 leading-[1.15]">
              Regional Partner
              <span className="italic text-[hsl(var(--primary)/0.7)]"> or </span>
              Memorial Partner.
            </h2>
            <p className="mt-6 text-[15.5px] leading-relaxed text-[hsl(var(--primary)/0.7)] font-light">
              Choose the role that fits your business. Regional Partners build a local network
              of providers; Memorial Partners (funeral homes, cemeteries) offer MyLeaf directly
              to the families they serve.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
            {/* Regional */}
            <div
              className="relative flex flex-col p-10 border border-[#1A3626]/40 rounded-sm"
              style={{ backgroundColor: '#1A3626' }}
              data-testid="partner-tier-regional"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#D9B775]">
                Regional Partner
              </div>
              <h3 className="font-serif font-light text-[30px] mt-4 text-[#F4E5C2] leading-tight">
                Build the local MyLeaf network.
              </h3>
              <p className="mt-5 text-sm text-[#F4E5C2]/85 font-light leading-relaxed">
                Recruit funeral homes and cemeteries into the MyLeaf network in your region.
                You earn on memorials you sell directly, and on every memorial your Memorial
                Partners sell.
              </p>
              <ul className="mt-8 space-y-3 text-[14px] text-[#F4E5C2]/95 font-light">
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#D9B775] shrink-0" />
                  $100 per direct family memorial sold
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#D9B775] shrink-0" />
                  $25 override per memorial sold by your Memorial Partners
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#D9B775] shrink-0" />
                  Unique invite code to onboard providers under your network
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#D9B775] shrink-0" />
                  Stripe-secured automatic payouts
                </li>
              </ul>
              <button
                type="button"
                onClick={() => { setAccountType('regional_partner'); scrollToForm(); }}
                className="mt-10 inline-flex items-center justify-center gap-2 rounded-sm bg-[#D9B775] text-[#1A3626] px-8 py-3 text-[11px] font-semibold tracking-[0.22em] uppercase transition-all duration-300 hover:bg-[#F4E5C2]"
                data-testid="partner-tier-regional-cta"
              >
                Apply as a Regional Partner
              </button>
            </div>

            {/* Memorial */}
            <div
              className="relative flex flex-col p-10 border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--surface))] rounded-sm"
              data-testid="partner-tier-memorial"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[hsl(var(--accent))]">
                Memorial Partner
              </div>
              <h3 className="font-serif font-light text-[30px] mt-4 text-[hsl(var(--primary))] leading-tight">
                Offer MyLeaf directly to families you serve.
              </h3>
              <p className="mt-5 text-sm text-[hsl(var(--primary)/0.7)] font-light leading-relaxed">
                Funeral homes, cemeteries, and memorial businesses introduce MyLeaf to families
                during arrangement. You operate under a Regional Partner — they share a code
                with you when you sign up.
              </p>
              <ul className="mt-8 space-y-3 text-[14px] text-[hsl(var(--primary)/0.82)] font-light">
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                  $100 per memorial sold to families you introduce
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                  Branded materials &amp; co-marketing assets
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                  Family-friendly handouts and QR pricing options
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))] shrink-0" />
                  Stripe-secured automatic payouts
                </li>
              </ul>
              <button
                type="button"
                onClick={() => { setAccountType('memorial_partner'); scrollToForm(); }}
                className="mt-10 ml-btn-outline"
                data-testid="partner-tier-memorial-cta"
              >
                Apply as a Memorial Partner
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PAYOUT & TRUST */}
      <section className="ml-section-sacred">
        <div className="ml-container grid md:grid-cols-2 gap-16 items-start">
          <div>
            <div className="ml-overline">Trust &amp; payouts</div>
            <h2 className="font-serif font-light text-4xl text-[hsl(var(--primary))] mt-5 leading-[1.15]">
              Paid the moment the family completes checkout.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-[hsl(var(--primary)/0.7)] font-light">
              Commissions are computed automatically by the platform. Stripe Connect handles
              banking, identity verification, and year-end 1099 reporting — so partners never
              have to chase a payment or upload bank forms to us.
            </p>
            <ul className="mt-8 space-y-4 text-[14.5px] font-light text-[hsl(var(--primary)/0.85)]">
              <li className="flex items-start gap-3">
                <FileCheck2 className="w-5 h-5 text-[hsl(var(--accent))] mt-0.5 shrink-0" strokeWidth={1.4} />
                W-9 collected via Stripe Connect after approval — secure, encrypted, never shared with families.
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-[hsl(var(--accent))] mt-0.5 shrink-0" strokeWidth={1.4} />
                Refund safety: commissions reverse automatically if a family refunds.
              </li>
              <li className="flex items-start gap-3">
                <Banknote className="w-5 h-5 text-[hsl(var(--accent))] mt-0.5 shrink-0" strokeWidth={1.4} />
                Direct deposit to a verified business bank account, via Stripe.
              </li>
            </ul>
          </div>
          <div className="rounded-sm border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--surface))] p-8 sm:p-10">
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[hsl(var(--accent))]">
              How a memorial sale flows
            </div>
            <ol className="mt-6 space-y-6 text-[14.5px] font-light text-[hsl(var(--primary)/0.82)]">
              <li className="flex gap-4">
                <span className="font-serif text-2xl text-[hsl(var(--primary))]">01</span>
                <div>
                  A family creates a memorial using your code or through a funeral home you've referred.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="font-serif text-2xl text-[hsl(var(--primary))]">02</span>
                <div>
                  They complete checkout. Stripe captures the payment and notifies MyLeaf.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="font-serif text-2xl text-[hsl(var(--primary))]">03</span>
                <div>
                  Your commission is recorded, split if applicable, and queued for payout via Stripe Connect.
                </div>
              </li>
              <li className="flex gap-4">
                <span className="font-serif text-2xl text-[hsl(var(--primary))]">04</span>
                <div>
                  Funds land in your business account on Stripe's normal payout schedule.
                </div>
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section
        ref={formRef}
        id="partner-apply"
        className="ml-section-sacred"
        data-testid="partner-apply-section"
      >
        <div className="ml-container">
          <div className="max-w-3xl mx-auto">
            <div className="text-center">
              <div className="ml-overline">Apply now</div>
              <h2 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-5 leading-[1.15]">
                Partner application.
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-[hsl(var(--primary)/0.65)] font-light max-w-xl mx-auto">
                Applications are reviewed by our team within two business days. Once approved
                you'll receive a Stripe Connect link to finish onboarding.
              </p>
            </div>

            <form
              onSubmit={submit}
              className="mt-12 space-y-6 wizard-readable"
              data-testid="partner-register-form"
            >
              {/* Role selector */}
              <div>
                <label className="ml-label !text-[hsl(var(--primary))] !font-bold">Partner role</label>
                <div className="grid sm:grid-cols-2 gap-3 mt-2" data-testid="partner-role-row">
                  {[
                    { id: 'regional_partner', title: 'Regional Partner', body: 'Recruit funeral homes and cemeteries into the MyLeaf network.' },
                    { id: 'memorial_partner', title: 'Memorial Partner', body: 'Funeral home, cemetery, or memorial business under a Regional Partner.' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setAccountType(opt.id)}
                      data-testid={`partner-role-${opt.id}`}
                      className={`text-left rounded-md border-2 p-4 transition ${
                        accountType === opt.id
                          ? 'border-[#B8943F] bg-[#D9B775] text-[#1A3626] shadow-md'
                          : 'border-[hsl(var(--primary))]/30 bg-white text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))] hover:bg-[#FDFBF7]'
                      }`}
                    >
                      <div className="text-sm font-bold tracking-wide">{opt.title}</div>
                      <div className={`text-[12px] mt-1.5 leading-relaxed font-medium ${
                        accountType === opt.id ? 'text-[#1A3626]/85' : 'text-[hsl(var(--primary))]/75'
                      }`}>
                        {opt.body}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Memorial Partner — invite code */}
              {isMemorial && (
                <div>
                  {/* iter89 — Surface RP attribution prominently when the
                      funeral home arrived via a trackable invite link.
                      Server-side attribution is already locked via the
                      token; this banner removes the "RP code is empty,
                      did the link work?" confusion. */}
                  {inviteTokenInfo?.valid && (
                    <div
                      className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4"
                      data-testid="partner-invite-attribution-banner"
                    >
                      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-emerald-800">
                        Invitation verified
                      </div>
                      <div className="mt-1 text-sm text-emerald-900 leading-snug">
                        You've been invited by{' '}
                        <strong>{inviteTokenInfo.inviter_business_name || 'a MyLeaf Regional Partner'}</strong>
                        {inviteTokenInfo.inviter_invite_code ? (
                          <> — their invite code <span className="font-mono tracking-wider">{inviteTokenInfo.inviter_invite_code}</span> has been pre-filled below.</>
                        ) : '.'}
                        {' '}They will be your agent of record for any families enrolled under your funeral home.
                      </div>
                      {/* iter103 — escape hatch: some partners sent FH
                          invites to families by mistake. Attribution is
                          preserved either way (backend accepts the token
                          on family signups too). */}
                      <div className="mt-2 text-xs text-emerald-900/80">
                        Not a funeral home?{' '}
                        <a
                          href={`/register?inv=${encodeURIComponent(inviteToken)}&kind=family`}
                          className="font-semibold underline underline-offset-2"
                          data-testid="invite-switch-to-family"
                        >
                          Begin a family memorial instead
                        </a>
                        {' '}— your invitation still counts.
                      </div>
                    </div>
                  )}
                  <label className="ml-label">Regional Partner invite code</label>
                  <input
                    required
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                    inputMode="numeric"
                    pattern="[0-9]{4}"
                    maxLength={4}
                    className="ml-input font-mono tracking-[0.5em] text-center text-lg"
                    placeholder="1234"
                    data-testid="partner-invite-code"
                  />
                  <p className="mt-1 text-[11px] text-[hsl(var(--primary))]/65 font-medium">
                    A 4-digit code given to you by the Regional Partner who introduced you to MyLeaf.
                  </p>
                  {inviteValid && (
                    <p
                      className={`mt-1 text-[12px] font-medium ${inviteValid.valid ? 'text-emerald-700' : 'text-red-700'}`}
                      data-testid="partner-invite-feedback"
                    >
                      {inviteValid.valid
                        ? `Verified — you'll be linked under ${inviteValid.regional_partner_name}.`
                        : 'Invite code not recognised. Double-check with your Regional Partner.'}
                    </p>
                  )}
                </div>
              )}

              {/* iter53 — Regional Partner type chooser. Memorial Partners
                  skip this since they're always business entities (funeral
                  homes / cemeteries). */}
              {!isMemorial && (
                <div className="space-y-3 rounded-md border border-[hsl(var(--primary))]/12 bg-[#FDFBF7] p-6">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
                    Partner type
                  </div>
                  <p className="text-[12.5px] text-[hsl(var(--primary))]/65 leading-relaxed">
                    Regional Partners can sign up as an individual sole proprietor or as a registered business.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { id: 'individual', title: 'Individual / Sole Proprietor', body: 'You file taxes under your own SSN.' },
                      { id: 'business', title: 'LLC / Corporation', body: 'You file under an EIN registered to a business entity.' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setPartnerType(opt.id)}
                        data-testid={`partner-type-${opt.id}`}
                        className={`text-left rounded-md border-2 p-4 transition ${
                          partnerType === opt.id
                            ? 'border-[#B8943F] bg-[#D9B775] text-[#1A3626] shadow-md'
                            : 'border-[hsl(var(--primary))]/30 bg-white text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]'
                        }`}
                      >
                        <div className="text-sm font-bold tracking-wide">{opt.title}</div>
                        <div className={`text-[12px] mt-1.5 leading-relaxed font-medium ${
                          partnerType === opt.id ? 'text-[#1A3626]/85' : 'text-[hsl(var(--primary))]/75'
                        }`}>
                          {opt.body}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Business */}
              <div className="space-y-4 rounded-md border border-[hsl(var(--primary))]/12 bg-[#FDFBF7] p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))]">
                  Business
                </div>
                <div>
                  <label className="ml-label">
                    Business name{' '}
                    {!isMemorial && partnerType === 'individual' && (
                      <span className="text-[10px] tracking-wider font-normal text-[hsl(var(--primary))]/55 ml-1">
                        (optional — leave blank to use your legal name)
                      </span>
                    )}
                  </label>
                  <input
                    required={isMemorial || partnerType !== 'individual'}
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    className="ml-input"
                    placeholder={!isMemorial && partnerType === 'individual'
                      ? 'e.g. your trading name (optional)'
                      : 'e.g. Hall Funeral Home'}
                    data-testid="partner-business-name"
                  />
                </div>
                <div>
                  <label className="ml-label">Business type</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                    {BUSINESS_TYPES.map((bt) => (
                      <button
                        type="button"
                        key={bt.id}
                        onClick={() => setBusinessType(bt.id)}
                        data-testid={`partner-business-type-${bt.id}`}
                        className={`rounded-sm border px-3 py-2 text-[11px] tracking-wide font-semibold transition ${
                          businessType === bt.id
                            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[#F4E5C2]'
                            : 'border-[hsl(var(--primary))]/25 text-[hsl(var(--primary))] hover:border-[hsl(var(--primary))]'
                        }`}
                      >
                        {bt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Primary contact */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="ml-label">Primary contact</label>
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="ml-input"
                    placeholder="Person we contact about this account"
                    data-testid="partner-name-input"
                  />
                </div>
                <div>
                  <label className="ml-label">Phone</label>
                  <input
                    required
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="ml-input"
                    placeholder="+1 555 123 4567"
                    data-testid="partner-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="ml-label">Business email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ml-input"
                  data-testid="partner-email-input"
                />
                {(() => {
                  const sug = suggestCorrectEmail(email);
                  if (!sug) return null;
                  return (
                    <div className="mt-1.5 text-xs text-amber-700 flex items-center gap-1.5" data-testid="partner-email-typo-hint">
                      <span>Did you mean</span>
                      <button
                        type="button"
                        onClick={() => setEmail(sug)}
                        className="font-semibold underline underline-offset-2 hover:text-amber-900"
                        data-testid="partner-email-typo-fix"
                      >{sug}</button>
                      <span>?</span>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="ml-label">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="ml-input !pr-10"
                    data-testid="partner-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[hsl(var(--primary)/0.55)] hover:text-[hsl(var(--primary))] transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="partner-password-toggle"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                  </button>
                </div>
              </div>

              {/* iter94 — Multi-country tax info block. Tax Country
                  drives which sibling fields are required + visible. */}
              <div className="rounded-md border border-[hsl(var(--primary))]/12 bg-[#FDFBF7] p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))] mb-4">
                  Tax information
                </div>
                <TaxFieldsForm
                  value={{
                    tax_country: taxCountry,
                    tax_contractor_type: taxContractorType,
                    legal_business_name: legalBusinessName,
                    tax_id: taxId,
                    tax_sin: taxSin,
                    tax_business_number: taxBusinessNumber,
                    tax_corporation_name: taxCorporationName,
                    tax_foreign_id: taxForeignId,
                    tax_business_registration: taxBusinessRegistration,
                  }}
                  onChange={(v) => {
                    setTaxCountry(v.tax_country || 'US');
                    setTaxContractorType(v.tax_contractor_type || '');
                    setLegalBusinessName(v.legal_business_name || '');
                    setTaxId(v.tax_id || '');
                    setTaxSin(v.tax_sin || '');
                    setTaxBusinessNumber(v.tax_business_number || '');
                    setTaxCorporationName(v.tax_corporation_name || '');
                    setTaxForeignId(v.tax_foreign_id || '');
                    setTaxBusinessRegistration(v.tax_business_registration || '');
                  }}
                  mode="signup"
                />
                <label
                  className="mt-5 flex items-start gap-3 cursor-pointer"
                  data-testid="partner-tax-confirmation-row"
                >
                  <input
                    type="checkbox"
                    checked={taxConfirmation}
                    onChange={(e) => setTaxConfirmation(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[hsl(var(--accent))]"
                    data-testid="partner-tax-confirmation"
                  />
                  <span className="text-[12.5px] text-[hsl(var(--primary))] font-medium leading-snug">
                    I confirm this information is accurate for required tax reporting (1099 / T4A / equivalent).
                  </span>
                </label>
              </div>

              {/* Address */}
              <div className="rounded-md border border-[hsl(var(--primary))]/12 bg-[#FDFBF7] p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))] mb-3">
                  Business address
                </div>
                <div>
                  <label className="ml-label">Street address</label>
                  <input
                    required
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    className="ml-input"
                    placeholder="123 Main St"
                    data-testid="partner-address-line1"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  <div>
                    <label className="ml-label">City</label>
                    <input
                      required
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="ml-input"
                      data-testid="partner-city"
                    />
                  </div>
                  <div>
                    <label className="ml-label">State / Region</label>
                    <input
                      required
                      value={stateRegion}
                      onChange={(e) => setStateRegion(e.target.value)}
                      className="ml-input"
                      data-testid="partner-state"
                    />
                  </div>
                  <div>
                    <label className="ml-label">Postal code</label>
                    <input
                      required
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      className="ml-input"
                      data-testid="partner-postal"
                    />
                  </div>
                  <div>
                    <label className="ml-label">Country</label>
                    <input
                      required
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="ml-input"
                      data-testid="partner-country"
                    />
                  </div>
                </div>
              </div>

              {err && (
                <div className="text-sm text-[hsl(var(--destructive))]" data-testid="partner-error">
                  {err}
                </div>
              )}

              {/* iter53 — Independent Contractor Agreement. */}
              <div
                className="rounded-md border-2 border-[hsl(var(--primary))]/15 bg-[#FDFBF7] p-6"
                data-testid="independent-contractor-agreement"
              >
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[hsl(var(--primary))] mb-3">
                  Independent Partner Agreement
                </div>
                <div className="rounded-sm bg-white border border-[hsl(var(--primary))]/12 p-4 max-h-44 overflow-auto text-[12.5px] text-[hsl(var(--primary))]/85 leading-relaxed space-y-2">
                  <p>
                    By signing this agreement, you (the <strong>Partner</strong>) acknowledge and agree that:
                  </p>
                  <ol className="list-decimal pl-5 space-y-1.5">
                    <li>
                      You are an <strong>independent contractor</strong>, not an employee, agent,
                      or representative of MyLeaf Legacy LLC ("MyLeaf").
                    </li>
                    <li>
                      You are solely responsible for filing and paying all federal, state, and
                      local taxes on commissions you earn. MyLeaf does not withhold taxes.
                      You will receive an annual 1099 form via Stripe Connect.
                    </li>
                    <li>
                      Commissions are referral-based compensation only — no salary,
                      benefits, healthcare, retirement, or paid time off is provided.
                    </li>
                    <li>
                      MyLeaf does not guarantee any minimum earnings, lead volume, or
                      length of the partnership.
                    </li>
                    <li>
                      You will not represent yourself as an employee or official spokesperson
                      of MyLeaf in any communications with families, vendors, or other partners.
                    </li>
                    <li>
                      All payouts are processed by <strong>Stripe Connect Express</strong>.
                      MyLeaf does not handle banking information directly. Stripe is the
                      source of truth for identity verification, banking, payout schedule,
                      and 1099 issuance.
                    </li>
                    <li>
                      Either party may terminate this agreement at any time. Commissions
                      earned through accepted referrals up to the termination date will
                      still be paid out per Stripe's standard schedule.
                    </li>
                    <li>
                      <strong>Regional Partner override (12-month window).</strong> When
                      a Memorial Partner (funeral home, cemetery, or other memorial
                      business) joins MyLeaf using a Regional Partner's invite code or
                      invitation link, the Regional Partner earns a <strong>$25
                      override commission</strong> on every $199 or $299 family package
                      sold by that Memorial Partner for <strong>twelve (12) months</strong>
                      from the date MyLeaf approves the Memorial Partner's application.
                      After that twelve-month period the override expires automatically
                      for that specific Memorial Partner — the Memorial Partner continues
                      to earn their $100 direct commission on every sale, but the
                      Regional Partner no longer earns the $25 override on sales made by
                      that Memorial Partner. The override window is per-Memorial-Partner
                      and is calculated from each Memorial Partner's individual approval
                      date.
                    </li>
                  </ol>
                  <p className="text-[11px] text-[hsl(var(--primary))]/55 italic mt-2">
                    Agreement version {AGREEMENT_VERSION}. Your acceptance, the timestamp,
                    and the IP address you sign from will be logged for our shared audit trail.
                  </p>
                </div>
                <label
                  className="mt-4 flex items-start gap-3 cursor-pointer"
                  data-testid="agreement-accept-row"
                >
                  <input
                    type="checkbox"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[hsl(var(--accent))]"
                    data-testid="agreement-accept-checkbox"
                  />
                  <span className="text-[13px] text-[hsl(var(--primary))] font-medium leading-snug">
                    I have read and agree to the Independent Partner Agreement above. I
                    understand I am an independent contractor and responsible for my own
                    taxes.
                  </span>
                </label>

                {/* iter55 — typed full-name signature. Required to enable
                    submission alongside the checkbox. Rendered into the
                    signed PDF on the signature line in italic serif. */}
                <div
                  className="mt-5 rounded-md border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5 p-4"
                  data-testid="agreement-signature-block"
                >
                  <label className="block text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--accent))]">
                    Signature — type your full legal name
                  </label>
                  <input
                    type="text"
                    value={signatureFullName}
                    onChange={(e) => setSignatureFullName(e.target.value)}
                    placeholder="e.g. Jane Marie Doe"
                    autoComplete="off"
                    maxLength={120}
                    className="mt-2 w-full bg-transparent border-0 border-b-2 border-[hsl(var(--accent))] focus:outline-none focus:ring-0 px-1 pb-2 text-[20px] font-serif italic text-[hsl(var(--primary))]"
                    style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif' }}
                    data-testid="agreement-signature-input"
                  />
                  <p className="mt-2 text-[11px] text-[hsl(var(--primary))]/65 leading-snug">
                    Typing your name above is a legally binding electronic
                    signature acknowledging the Independent Partner Agreement.
                    Your typed name, IP address, and timestamp will be
                    captured and rendered into your signed PDF.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={
                  loading
                  || !taxConfirmation
                  || !agreementAccepted
                  || signatureFullName.trim().length < 3
                  || (isMemorial && (!inviteValid || !inviteValid.valid))
                }
                className="ml-btn-primary w-full !py-3.5"
                data-testid="partner-submit-btn"
              >
                {loading ? 'Submitting application…' : 'Submit partner application'}
              </button>

              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 text-center">
                Already a partner? <Link to="/login" className="text-[hsl(var(--primary))] font-medium" data-testid="partner-login-link">Sign in →</Link>
              </p>
              <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-1 text-center">
                Creating a memorial for a loved one instead?{' '}
                <Link to="/register" className="text-[hsl(var(--primary))] font-medium" data-testid="partner-family-redirect-link">
                  Family signup →
                </Link>
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
